import { ColumnDef } from "@tanstack/react-table";
import { Link } from "@heroui/link";
import type { TFunction } from "i18next";
import { toWorkspacePath } from "@/features/Workspaces/workspaceRoutes";

import { formatNumber } from "@/lib/utils";

import { getAuditColumns } from "@/features/Audit/getAuditColumns";

export type A7OPCVMComposition = {
  id: number;
  date: Date;
  a7_opcvm_strategy_id: number;
  a7_opcvm_sta7_opcvm_strategy_name: string;
  a7_opcvm_underlying_id: number;
  a7_opcvm_time_serie_file_id: number;
  valorisation_part_du_sous_jacent: number;
  detention_du_fonds_dans_le_sous_jacent: number;
  version: number;
  a7_opcvm_issuer_id?: number;
};

const tableName = "a7_opcvm_compositions";

export const getCompositionsColumns = (
  t: TFunction,
): ColumnDef<A7OPCVMComposition>[] => {
  const tTable = (key: string, defaultValue: string) =>
    t(`${tableName}.columns.${key}`, { ns: "tables", defaultValue });
  const tCommon = (key: string, defaultValue: string) =>
    t(`actions.${key}`, { ns: "common", defaultValue });

  return [
    {
      accessorKey: "id",
      header: tTable("id", "ID"),
    },
    {
      accessorKey: "date",
      header: tTable("date", "Date"),
    },
    {
      accessorKey: "a7_opcvm_strategy_name",
      header: tTable("a7_opcvm_strategy_name", "Fund"),
      cell: ({ row }) => {
        return (
          <Link
            href={toWorkspacePath(
              `/admin/a7-opcvm/a7_opcvm_strategies/${row.original.a7_opcvm_strategy_id}`,
            )}
            className="text-xs"
            underline="hover"
          >
            {row.getValue("a7_opcvm_strategy_name")}
          </Link>
        );
      },
    },
    {
      accessorKey: "a7_opcvm_underlying_id",
      header: tTable("a7_opcvm_underlying_id", "Financial instrument"),
    },
    {
      accessorKey: "a7_opcvm_issuer_name",
      header: tTable("a7_opcvm_issuer_name", "Issuer"),
      cell: ({ row }) => {
        return (
          <Link
            href={toWorkspacePath(
              `/admin/a7-opcvm/a7_opcvm_issuers/${row.original.a7_opcvm_issuer_id}`,
            )}
            className="text-xs"
            underline="hover"
          >
            {row.getValue("a7_opcvm_issuer_name")}
          </Link>
        );
      },
    },
    {
      accessorKey: "a7_opcvm_time_serie_file_id",
      header: tTable("a7_opcvm_time_serie_file_id", "File"),
      cell: ({ row }) => {
        return (
          <Link
            href={toWorkspacePath(
              `/admin/a7-opcvm/a7_opcvm_time_serie_files/${row.original.a7_opcvm_time_serie_file_id}`,
            )}
            className="text-xs"
            underline="hover"
          >
            {row.getValue("a7_opcvm_time_serie_file_id")}
          </Link>
        );
      },
    },
    {
      accessorKey: "valorisation_part_du_sous_jacent",
      header: () => (
        <div className="text-right">
          {tTable("valorisation_part_du_sous_jacent", "Valuation")}
        </div>
      ),
      cell: ({ getValue }) => {
        const value = getValue();
        return (
          value != null && (
            <div className="text-right">{formatNumber(value as number)}</div>
          )
        );
      },
    },
    {
      accessorKey: "detention_du_fonds_dans_le_sous_jacent",
      header: () => (
        <div className="text-right">
          {tTable("detention_du_fonds_dans_le_sous_jacent", "Holding")}
        </div>
      ),
      cell: ({ getValue }) => {
        const value = getValue();
        return (
          <div className="text-right">
            {value != null ? (value as number) : ""}
          </div>
        );
      },
    },
    {
      accessorKey: "version",
      header: () => (
        <div className="text-right">{tTable("version", "Version")}</div>
      ),
      cell: ({ getValue }) => (
        <div className="text-right">{getValue() as number}</div>
      ),
    },
    {
      id: "actions",
      cell: ({ row }) => {
        return (
          <div className="flex justify-end">
            <Link
              href={`${window.location.pathname}/${row.getValue("id")}`}
              className="text-xs"
              underline="hover"
            >
              {tCommon("edit", "Edit")}
            </Link>
          </div>
        );
      },
    },
  ];
};

export const getCompositionsAuditColumns = (t: TFunction) =>
  getAuditColumns<A7OPCVMComposition>(getCompositionsColumns(t)).filter(
    (column) => {
      const accessorKey =
        "accessorKey" in column
          ? String((column as { accessorKey?: unknown }).accessorKey ?? "")
          : "";
      return !["a7_opcvm_issuer_name", "a7_opcvm_strategy_name"].includes(
        accessorKey,
      );
    },
  );
